import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { useConfirmOtpMutation, useSendOtpMutation } from '@/features/api/authApi';
import { useAppDispatch, useAppSelector } from '@/hooks/useStore';
import { clearPendingVerification, setAuthenticated } from '@/features/auth/authSlice';
import { setAuthCookies } from '@/lib/cookies';
import { buildRedirectUrl } from '@/lib/redirect';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 32;

export default function VerifyOTP() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const pending = useAppSelector((s) => s.auth.pendingVerification);

    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [timer, setTimer] = useState(RESEND_SECONDS);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const verifiedRef = useRef(false);

    const [confirmOtp, { isLoading: isVerifying }] = useConfirmOtpMutation();
    const [sendOtp, { isLoading: isResending }] = useSendOtpMutation();

    const isComplete = useMemo(() => otp.every((v) => v.trim().length === 1), [otp]);
    const loginMethod = pending?.phoneNumber ? 'phone' : 'email';

    // Redirect if no pending verification context
    useEffect(() => {
        if (!pending && !verifiedRef.current) {
            navigate('/sign-in', { replace: true });
        }
    }, [pending, navigate]);

    // Auto-focus first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    // Countdown timer
    useEffect(() => {
        if (timer <= 0) return;
        const id = setInterval(() => setTimer((t) => t - 1), 1000);
        return () => clearInterval(id);
    }, [timer]);

    const focusInput = (index: number) => {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
    };

    const handleChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        setOtp((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        if (digit && index < OTP_LENGTH - 1) focusInput(index + 1);
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            setOtp((prev) => {
                const next = [...prev];
                if (next[index]) {
                    next[index] = '';
                    return next;
                }
                if (index > 0) {
                    focusInput(index - 1);
                    next[index - 1] = '';
                }
                return next;
            });
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            e.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
            if (!pasted) return;
            const next = [...otp];
            for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
            setOtp(next);
            focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
        },
        [otp],
    );

    const handleResend = async () => {
        if (!pending) return;
        setError('');
        setSuccess('');
        try {
            const body = pending.email ? { email: pending.email } : { phoneNumber: pending.phoneNumber! };
            await sendOtp(body).unwrap();
            setTimer(RESEND_SECONDS);
            setOtp(Array(OTP_LENGTH).fill(''));
            focusInput(0);
            setSuccess('OTP sent successfully.');
        } catch (err: unknown) {
            const apiErr = err as { data?: { message?: string; response?: { message?: string } } };
            setError(apiErr?.data?.response?.message ?? apiErr?.data?.message ?? 'Failed to resend OTP.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isComplete || isVerifying) return;
        setError('');
        setSuccess('');

        try {
            const result = await confirmOtp({ otp: otp.join('') }).unwrap();
            const token = result.response.token;
            setAuthCookies(token, result.response.data.id);

            const redirectUrl = pending?.redirectUrl;
            verifiedRef.current = true;
            dispatch(setAuthenticated(true));
            dispatch(clearPendingVerification());

            if (redirectUrl) {
                window.location.href = buildRedirectUrl(redirectUrl, token, result.response.data.id);
            } else {
                navigate('/products', { replace: true });
            }
        } catch (err: unknown) {
            const apiErr = err as { data?: { message?: string; response?: { message?: string } } };
            setError(apiErr?.data?.response?.message ?? apiErr?.data?.message ?? 'Invalid OTP. Please try again.');
        }
    };

    const formattedTime = `${String(Math.floor(timer / 60)).padStart(2, '0')}:${String(timer % 60).padStart(2, '0')}`;

    if (!pending && !verifiedRef.current) return null;

    return (
        <AuthLayout>
            <form onSubmit={handleSubmit} className="flex flex-col">
                <h1 className="text-2xl font-bold leading-tight tracking-[-0.5px] text-foreground sm:text-[32px] sm:leading-10">
                    Verify Your Account
                </h1>
                <p className="mt-2 text-xs leading-5 text-text-light sm:mt-3 sm:text-sm sm:leading-6">
                    {loginMethod === 'phone'
                        ? 'Type the 6-digit code sent to your WhatsApp. This helps keep your account secure.'
                        : 'Type the 6-digit code sent to your email. This helps keep your account secure.'}
                </p>

                {/* Error / Success */}
                {error && (
                    <div className="mt-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mt-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-600">
                        {success}
                    </div>
                )}

                {/* OTP Inputs */}
                <div className="mt-6 flex justify-center gap-2 sm:mt-10 sm:gap-3">
                    {otp.map((digit, i) => (
                        <input
                            key={i}
                            ref={(el) => {
                                inputRefs.current[i] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            aria-label={`Verification digit ${i + 1}`}
                            value={digit}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onPaste={handlePaste}
                            className="h-11 w-11 shrink-0 rounded-lg border border-input bg-background text-center text-lg font-semibold tracking-widest text-foreground outline-none transition-shadow focus:border-[#151515] focus:border-2 sm:h-14 sm:w-14 sm:text-2xl"
                        />
                    ))}
                </div>

                {/* Verify Button */}
                <button
                    type="submit"
                    disabled={!isComplete || isVerifying}
                    className="mt-6 h-11 w-full cursor-pointer rounded-full bg-btn text-sm font-bold text-btn-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:mt-8 sm:h-12"
                >
                    {isVerifying ? 'Verifying...' : 'Verify Account'}
                </button>

                {/* Resend */}
                <p className="mt-4 text-center text-sm leading-6 text-text-light">
                    Didn&rsquo;t get the code?{' '}
                    {timer > 0 ? (
                        <span className="font-bold text-primary">Resend OTP in {formattedTime}</span>
                    ) : (
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={isResending}
                            className="cursor-pointer font-bold text-primary hover:underline disabled:opacity-60"
                        >
                            {isResending ? 'Sending...' : 'Resend OTP'}
                        </button>
                    )}
                </p>
            </form>
        </AuthLayout>
    );
}
